import { cloneElement } from 'react';
import type { FC } from 'react';
import { isAnalyzing } from '../../helpers/revisions';
import type { RouterProps } from '../../types';
import type { ProjectDashboardProject, ProjectDashboardRevision } from './projectDashboardTypes';
import { RouterLink } from '../generic/Link';
import { nuc } from '../../utils';

export type RevisionReadyContainerParams = {
  project_id?: string, // eslint-disable-line @typescript-eslint/naming-convention -- Param name already exists.
  revision_id?: string, // eslint-disable-line @typescript-eslint/naming-convention -- Param name already exists.
  reference_type?: string, // eslint-disable-line @typescript-eslint/naming-convention -- Param name already exists.
  reference?: string,
};

type RevisionReadyContainerProps = RouterProps<RevisionReadyContainerParams> & {
  project: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
  onProjectChange: () => void;
};

/**
 * Determines whether to show the placeholder.
 * The placeholder is shown if either:
 * 1. the revision is loading
 * 2. the revision is still analyzing
 */
function shouldShowPlaceholder(revision: ProjectDashboardRevision): boolean {
  return (
    revision.status.loading
    || !revision.data
    || isAnalyzing(revision.data)
  );
}

/**
 * Shows a placeholder banner if the revision is still loading/analyzing.
 */
export const RevisionReadyContainer: FC<RevisionReadyContainerProps> = (
  { project, revision, children, params, onProjectChange },
) => {
  // Don't show the banner if we aren't in a project context.
  if (!params.project_id || !params.reference || !params.revision_id || !params.reference_type) {
    return null;
  }

  if (shouldShowPlaceholder(revision)) {
    // NOTE: This route duplicates logic in DisplayProjectNav.  That component
    // needs to be rewritten and the routing logic extracted.
    const baseUrl = `/projects/${nuc(params.project_id)}/`;
    const revisionUrl = `${baseUrl}refs/${params.reference_type}/${nuc(params.reference)}/${nuc(params.revision_id)}`;
    const dependenciesUrl = `${revisionUrl}/browse/dependencies`;

    return (
      <div className="container container-responsive">
        <div className="margin-top">
          <RouterLink to={dependenciesUrl} className="revision-status-banner__link">
            <div className="alert alert-warning">
              Fossa is still analyzing dependencies for this revision.  View the progress <u>here</u>.
            </div>
          </RouterLink>
        </div>
      </div>
    );
  }

  return cloneElement(children as any, {
    project,
    onProjectChange,
    revision,
  });
};
